import React from 'react';
import { CrgStatement } from '../types';

/**
 * Rendu A4 du Compte Rendu de Gestion, destiné à la conversion PDF via
 * window.html2pdf (même pattern que ContractBuilder / ProposalGenerator).
 */

const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const fmtEur = (n: number) =>
  (n || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const cellStyle: React.CSSProperties = {
  border: '1px solid #cbd5e1',
  padding: '4px 6px',
  fontSize: '9px',
  verticalAlign: 'top',
};

const headCellStyle: React.CSSProperties = {
  ...cellStyle,
  backgroundColor: '#0f172a',
  color: '#ffffff',
  fontWeight: 700,
  textAlign: 'left',
};

const numCellStyle: React.CSSProperties = { ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' };

export const CrgPdfDocument: React.FC<{ statement: CrgStatement }> = ({ statement }) => {
  const h = statement.headerSnapshot || {};
  const monthLabel = `${MONTHS_FR[(statement.periodMonth - 1) % 12]} ${statement.periodYear}`;
  const visibleLines = statement.lines.filter(l => !l.excluded);
  const totalCityTax = Math.round(visibleLines.reduce((s, l) => s + (l.cityTax || 0), 0) * 100) / 100;

  return (
    <div
      className="a4-page"
      style={{
        width: '210mm',
        minHeight: '297mm',
        padding: '15mm',
        backgroundColor: '#ffffff',
        color: '#0f172a',
        fontFamily: 'Helvetica, Arial, sans-serif',
        fontSize: '10px',
        boxSizing: 'border-box',
      }}
    >
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8mm' }}>
        <div>
          <h1 style={{ fontSize: '16px', fontWeight: 800, margin: 0 }}>Compte Rendu de Gestion</h1>
          <p style={{ fontSize: '12px', fontWeight: 700, color: '#b45309', margin: '2px 0 0 0' }}>{monthLabel}</p>
        </div>
        <div style={{ textAlign: 'right', fontSize: '9px', color: '#475569' }}>
          <p style={{ fontWeight: 700, fontSize: '11px', color: '#0f172a', margin: 0 }}>{h.agencyName || ''}</p>
          {h.agencyAddress && <p style={{ margin: '1px 0 0 0' }}>{h.agencyAddress}</p>}
          {h.agencyCity && <p style={{ margin: '1px 0 0 0' }}>{h.agencyCity}</p>}
          {h.agencySiret && <p style={{ margin: '1px 0 0 0' }}>SIRET : {h.agencySiret}</p>}
        </div>
      </div>

      {/* Blocs propriétaire / bien */}
      <div style={{ display: 'flex', gap: '6mm', marginBottom: '8mm' }}>
        <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4mm' }}>
          <p style={{ fontSize: '8px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', margin: '0 0 2px 0' }}>Propriétaire</p>
          <p style={{ fontWeight: 700, margin: 0 }}>{h.ownerName || '—'}</p>
          {h.ownerAddress && <p style={{ margin: '1px 0 0 0', color: '#475569' }}>{h.ownerAddress}</p>}
          {(h.ownerZipCode || h.ownerCity) && (
            <p style={{ margin: '1px 0 0 0', color: '#475569' }}>{[h.ownerZipCode, h.ownerCity].filter(Boolean).join(' ')}</p>
          )}
        </div>
        <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '4px', padding: '4mm' }}>
          <p style={{ fontSize: '8px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', margin: '0 0 2px 0' }}>Bien</p>
          <p style={{ fontWeight: 700, margin: 0 }}>{h.propertyName || '—'}</p>
          {h.propertyAddress && <p style={{ margin: '1px 0 0 0', color: '#475569' }}>{h.propertyAddress}</p>}
          {h.propertyCity && <p style={{ margin: '1px 0 0 0', color: '#475569' }}>{h.propertyCity}</p>}
        </div>
      </div>

      {/* Tableau des réservations */}
      {visibleLines.length === 0 ? (
        <div style={{ border: '1px dashed #cbd5e1', borderRadius: '4px', padding: '10mm', textAlign: 'center', color: '#64748b', marginBottom: '8mm' }}>
          Aucune réservation sur la période.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '8mm' }}>
          <thead>
            <tr>
              <th style={headCellStyle}>Plateforme</th>
              <th style={headCellStyle}>Voyageur</th>
              <th style={headCellStyle}>Arrivée</th>
              <th style={headCellStyle}>Départ</th>
              <th style={{ ...headCellStyle, textAlign: 'right' }}>Versé au client</th>
              <th style={{ ...headCellStyle, textAlign: 'right' }}>Frais de ménage</th>
              <th style={{ ...headCellStyle, textAlign: 'right' }}>Taxe séjour</th>
              <th style={{ ...headCellStyle, textAlign: 'right' }}>CA NET</th>
              <th style={{ ...headCellStyle, textAlign: 'right' }}>Com. %</th>
              <th style={{ ...headCellStyle, textAlign: 'right' }}>Commission</th>
            </tr>
          </thead>
          <tbody>
            {visibleLines.map((line, i) => (
              <tr key={i} style={{ pageBreakInside: 'avoid', backgroundColor: i % 2 === 1 ? '#f8fafc' : '#ffffff' }}>
                <td style={cellStyle}>{line.source}</td>
                <td style={cellStyle}>
                  {line.guestName}
                  {line.isCancellationFee && (
                    <span style={{ color: '#b91c1c', fontWeight: 700 }}> (frais d'annulation)</span>
                  )}
                  {line.manual && line.note && (
                    <span style={{ color: '#64748b', fontStyle: 'italic' }}> — {line.note}</span>
                  )}
                </td>
                <td style={cellStyle}>{fmtDate(line.checkinDate)}</td>
                <td style={cellStyle}>{fmtDate(line.checkoutDate)}</td>
                <td style={numCellStyle}>{fmtEur(line.paidOut)}</td>
                <td style={numCellStyle}>{fmtEur(line.cleaningFee)}</td>
                <td style={numCellStyle}>{fmtEur(line.cityTax || 0)}</td>
                <td style={numCellStyle}>{fmtEur(line.netRevenue)}</td>
                <td style={numCellStyle}>{line.commissionRate} %</td>
                <td style={numCellStyle}>{fmtEur(line.commissionAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Totaux */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', pageBreakInside: 'avoid' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: '80mm' }}>
          <tbody>
            <tr>
              <td style={{ ...cellStyle, fontWeight: 700 }}>Total versé au client</td>
              <td style={numCellStyle}>{fmtEur(statement.totalPaidOut)}</td>
            </tr>
            <tr>
              <td style={{ ...cellStyle, fontWeight: 700 }}>Total frais de ménage</td>
              <td style={numCellStyle}>{fmtEur(statement.totalCleaning)}</td>
            </tr>
            {totalCityTax > 0 && (
              <tr>
                <td style={{ ...cellStyle, fontWeight: 700 }}>Total taxe de séjour (non commissionnée)</td>
                <td style={numCellStyle}>{fmtEur(totalCityTax)}</td>
              </tr>
            )}
            <tr>
              <td style={{ ...cellStyle, fontWeight: 700 }}>Total CA NET</td>
              <td style={numCellStyle}>{fmtEur(statement.totalNetRevenue)}</td>
            </tr>
            <tr>
              <td style={{ ...cellStyle, fontWeight: 700 }}>Total commission ({statement.commissionRate} %)</td>
              <td style={numCellStyle}>{fmtEur(statement.totalCommission)}</td>
            </tr>
            {statement.rebillCleaning && (
              <tr>
                <td style={{ ...cellStyle, fontWeight: 700 }}>Refacturation ménage</td>
                <td style={numCellStyle}>{fmtEur(statement.totalCleaning)}</td>
              </tr>
            )}
            <tr>
              <td style={{ ...cellStyle, fontWeight: 800, backgroundColor: '#fef3c7' }}>Total à facturer au propriétaire</td>
              <td style={{ ...numCellStyle, fontWeight: 800, backgroundColor: '#fef3c7' }}>{fmtEur(statement.totalBilled)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Pied de page */}
      <p style={{ marginTop: '10mm', fontSize: '8px', color: '#94a3b8', textAlign: 'center' }}>
        Document généré par Placid le {new Date().toLocaleDateString('fr-FR')} — ne vaut pas facture.
      </p>
    </div>
  );
};
